import { CommitmentTargetRowSchema } from 'src/Ghg/interfaces';
// eslint-disable-next-line import/no-extraneous-dependencies
import PoppinsRegularUrl from 'src/views/Report/Pdf/fonts/Poppins-Regular.ttf';
import { AnchorMode, AnchorSpec } from './pdfAnchors';
import { stampPdf, downloadPdfBytes, PdfDraw } from './pdfStamp';
import {
  resolvePdfLanguage,
  SupplierCommitmentPdfLanguage,
  buildCommonFieldDraws,
  formatPdfDate,
} from './PdfDocument';
import { formatStatementDecimal } from './statementFormat';

// C17 (specs/001-raben-country-masteradmin/mass-specs/specs/20260826-decl-
// target-form.md, Goal 4): stamps the target statement's fillable blanks
// onto page 1 of the REAL source PDF (reference/docs/sbti-oswiadczenie-
// wyznaczenie-celow-PL.pdf / sbti-statement-target-setting-EN.pdf, served
// as public/assets/raben/target-statement-{pl,en}.pdf) with pdf-lib --
// same pivot and same reasoning as PdfDocument.ts (C16): a react-pdf
// recreation both mangled Polish glyphs and never quite matched the
// source's layout, so this stamps the real page instead. See pdfStamp.ts
// for the drawing mechanics and PdfDocument.ts for the shared 4-field
// (company name / tax id / date / signature) anchors both documents use.
//
// The statement has 7 more blanks beyond those 4, and BASE_YEAR and
// TARGET_YEAR are each filled in TWICE (once while defining the base year
// and time horizon, once again in the reduction-target sentence) -- see
// the anchor list below, each one verified to match exactly one place in
// both source PDFs' text layers. Every value here is either server-
// computed (BR-1: target_year/reduction_pct/target_emissions are derived
// from base_year alone, never from anything the client sends) or frozen at
// set time (BR-3, mirroring RabenConsentSnapshotSchema) -- this component
// never computes or re-derives any of the figures, only draws them.

export interface CommitmentTargetPdfProps {
  target: CommitmentTargetRowSchema;
  language: string;
}

export const TARGET_STATEMENT_PAGE = 1;

export const TARGET_STATEMENT_SOURCE_URL: Record<
  SupplierCommitmentPdfLanguage,
  string
> = {
  pl: '/assets/raben/target-statement-pl.pdf',
  en: '/assets/raben/target-statement-en.pdf',
  de: '/assets/raben/target-statement-de.pdf',
};

// Anchor phrases for the 7 target-specific blanks, one table per language.
// base_year and target_year each get drawn twice (the document states them
// once when defining the base year/horizon, and again in the reduction
// sentence) -- both occurrences are listed explicitly rather than derived,
// since the two languages wrap those sentences differently: the EN
// reduction sentence keeps its blank on the same line as the preceding
// words ("...emissions by ...... % until ...... , compared to..."), but
// the PL translation wraps so the percentage blank OPENS a new line with
// nothing before it on that line -- hence anchoring that one blank on the
// "%" that follows it (mode: 'before') instead of on preceding text, which
// works identically in both languages.
interface TargetAnchors {
  baseYearFirst: string;
  targetYearFirst: string;
  reductionPctBefore: string;
  targetYearSecond: string;
  baseYearSecond: string;
  baseEmissions: string;
  targetEmissions: string;
  // How the two emission values are placed relative to their labels.
  // 'afterBlank' where the label is padded with spaces before its dots (DE).
  emissionsMode: AnchorMode;
}

const TARGET_ANCHORS: Record<SupplierCommitmentPdfLanguage, TargetAnchors> = {
  pl: {
    baseYearFirst: 'Przewoźnik wyznacza rok',
    targetYearFirst: ', obejmujący okres do roku',
    reductionPctBefore: '%',
    targetYearSecond: '% do roku',
    baseYearSecond: ', w odniesieniu do poziomu emisji z roku bazowego',
    baseEmissions: 'Poziom emisji w roku bazowym:',
    targetEmissions: 'Poziom emisji do osiągnięcia w roku docelowym:',
    emissionsMode: 'after',
  },
  en: {
    baseYearFirst: 'Carrier designates',
    targetYearFirst: 'covering the period up to',
    reductionPctBefore: '%',
    targetYearSecond: 'until',
    baseYearSecond: 'compared to the',
    baseEmissions: 'Emissions level in the base year:',
    targetEmissions: 'Emissions level to be achieved in the target year:',
    emissionsMode: 'after',
  },
  // Verified against the official German statement's text layer
  // (2026-08-27): each phrase matches exactly one line on page 1.
  // "Treibhausgasemissionen" alone appears four times, hence the longer
  // "...bis" phrase for the reduction sentence's target year; the one "%"
  // on the page is the reduction blank's own.
  de: {
    baseYearFirst: 'legt das Jahr',
    targetYearFirst: 'der den Zeitraum bis',
    reductionPctBefore: '%',
    targetYearSecond: 'Treibhausgasemissionen bis',
    baseYearSecond: 'gegenüber dem Referenzwert von',
    baseEmissions: 'Emissionsniveau im Basisjahr:',
    targetEmissions: 'Im Zieljahr zu erreichendes Emissionsniveau:',
    // RG 28.08 12.1: both DE labels are followed by trailing spaces (up to
    // x~319 on the base-year line, x~290 on the target-year one) before the
    // dots start at x=318.65, so 'after' put the values ~30-100pt left of
    // the blank.
    emissionsMode: 'afterBlank',
  },
};

function buildTargetSpecificDraws(
  pdfLanguage: SupplierCommitmentPdfLanguage,
  target: CommitmentTargetRowSchema
): PdfDraw[] {
  const a = TARGET_ANCHORS[pdfLanguage];
  const baseYear = String(target.base_year);
  const targetYear = String(target.target_year);
  const spec = (phrase: string, mode: AnchorSpec['mode']): AnchorSpec => ({
    phrase,
    mode,
  });
  return [
    { spec: spec(a.baseYearFirst, 'after'), value: baseYear },
    { spec: spec(a.targetYearFirst, 'after'), value: targetYear },
    {
      spec: spec(a.reductionPctBefore, 'before'),
      value: formatStatementDecimal(target.reduction_pct, pdfLanguage),
    },
    { spec: spec(a.targetYearSecond, 'after'), value: targetYear },
    { spec: spec(a.baseYearSecond, 'after'), value: baseYear },
    {
      spec: spec(a.baseEmissions, a.emissionsMode),
      value: formatStatementDecimal(target.base_emissions, pdfLanguage),
    },
    {
      spec: spec(a.targetEmissions, a.emissionsMode),
      value: formatStatementDecimal(target.target_emissions, pdfLanguage),
    },
  ];
}

// Every value stamped onto the statement (the 7 target-specific blanks plus
// the 4 shared fields). Exported so pdfTemplates.test.ts resolves exactly
// the anchors the download stamps.
export function buildTargetStatementDraws(
  pdfLanguage: SupplierCommitmentPdfLanguage,
  target: CommitmentTargetRowSchema
): PdfDraw[] {
  return [
    ...buildTargetSpecificDraws(pdfLanguage, target),
    ...buildCommonFieldDraws(pdfLanguage, {
      companyName: target.company_name,
      taxId: target.vat_prefixed,
      date: formatPdfDate(pdfLanguage, target.created_at),
      signature: target.company_name,
    }),
  ];
}

export function buildCommitmentTargetFilename(
  target: CommitmentTargetRowSchema
): string {
  // Deterministic on purpose (BR-3, mirroring PdfDocument.ts's
  // buildSupplierCommitmentFilename): the SET date, not "today", so a
  // later regeneration from the same stored row downloads a file with the
  // same name every time.
  const day = target.created_at.slice(0, 10);
  return `Target_Statement_${target.vat_prefixed}_${day}.pdf`;
}

// Goal 4: "auto-download on accept" -- same pattern as PdfDocument.ts's
// downloadSupplierCommitmentPdf, so both the initial set and a later
// re-download from the stored row trigger identically.
export async function downloadCommitmentTargetPdf(
  props: CommitmentTargetPdfProps
): Promise<void> {
  const { target, language } = props;
  const pdfLanguage = resolvePdfLanguage(language);

  const bytes = await stampPdf({
    sourceUrl: TARGET_STATEMENT_SOURCE_URL[pdfLanguage],
    pageNumber: TARGET_STATEMENT_PAGE,
    draws: buildTargetStatementDraws(pdfLanguage, target),
    fontUrl: PoppinsRegularUrl,
  });

  downloadPdfBytes(bytes, buildCommitmentTargetFilename(target));
}
